<script setup lang="ts">
import { scopeWpHtml } from '@/utils/scopeWpHtml';
import { executeWpScripts } from '@/utils/executeWpScripts';
import {
  fetchBlogPostsHandler,
  clearBlogPostsCache,
} from '~/handlers/fetch-blog-posts.handler';
import { fetchBlogPostHandler } from '~/handlers/fetch-blog-post.handler';
import { getPromotionsCategoryId } from '~/utils/apostatudo/get-promotions-category-id';
import { useUserJourney } from '~/composables/apostatudo/use-user-journey';
import {
  mapCategoriesToWp,
  mapPostToWp,
  type WpStylePost,
  type WpStyleCategory,
} from '~/utils/apostatudo/map-core-to-wp';

const route = useRoute('blog-post');

const { blogPostClickEvent } = useUserJourney();
const { isAuthenticated } = storeToRefs(useAuthStore());

const promoSlugs = ref<Set<string>>(new Set());
const promoCategoryId = ref<string | null>(null);

function isPromoCategoryName(name: string | undefined | null): boolean {
  return (name || '').toLowerCase().includes('promo');
}

const isLoading = ref<boolean>(false);
const isLoadingDetail = ref<boolean>(false);
const isLoadingCategories = ref<boolean>(false);
const wpPosts = ref<WpStylePost[]>([]);
const wpCategories = ref<WpStyleCategory[]>([]);
const detailItem = ref<WpStylePost | null>(null);
const selectedCategory = ref<string | null>(null);

const postSlug = computed<string>(() => {
  const raw = route.params.post;

  return Array.isArray(raw) ? raw[0] : raw || '';
});

const singleItem = computed<WpStylePost | null>(() => {
  if (!postSlug.value) return null;

  if (detailItem.value && detailItem.value.slug === postSlug.value) {
    return detailItem.value;
  }

  return wpPosts.value.find((p) => p.slug === postSlug.value) ?? null;
});

const STATE_ORDER: Record<string, number> = { ACTIVE: 0, ENDED: 1 };

function sortPosts(posts: WpStylePost[]): WpStylePost[] {
  return [...posts]
    .filter((p) => p.state === 'ACTIVE' || p.state === 'ENDED')
    .sort(
      (a, b) => (STATE_ORDER[a.state] ?? 99) - (STATE_ORDER[b.state] ?? 99),
    );
}

const visiblePosts = computed<WpStylePost[]>(() => {
  if (isAuthenticated.value) return wpPosts.value;

  return wpPosts.value.filter((post) => !promoSlugs.value.has(post.slug));
});

const visibleCategories = computed<WpStyleCategory[]>(() => {
  if (isAuthenticated.value) return wpCategories.value;

  return wpCategories.value.filter((cat) => cat.id !== promoCategoryId.value);
});

const filteredPosts = computed(() => {
  if (!selectedCategory.value) {
    return sortPosts(visiblePosts.value);
  }

  return sortPosts(
    visiblePosts.value.filter((post) =>
      post.categories.includes(selectedCategory.value as string),
    ),
  );
});

const featuredCategoryId = computed<string | null>(() => {
  const cat = visibleCategories.value.find(
    (c) => c.name?.toLowerCase().trim() === 'destaques',
  );

  return cat?.id ?? null;
});

const featuredPost = computed<WpStylePost | null>(() => {
  if (!visiblePosts.value?.length || !featuredCategoryId.value) return null;

  return (
    visiblePosts.value.find((post) =>
      post.categories.includes(featuredCategoryId.value as string),
    ) || null
  );
});

const isBannerSpecial = computed(() => featuredPost.value !== null);

const regularPosts = computed(() => {
  if (selectedCategory.value) {
    return filteredPosts.value;
  }

  const actives = visibleCategories.value.flatMap((cat) =>
    visiblePosts.value.filter(
      (post) => post.categories.includes(cat.id) && post.state === 'ACTIVE',
    ),
  );

  const ended = visibleCategories.value.flatMap((cat) =>
    visiblePosts.value.filter(
      (post) => post.categories.includes(cat.id) && post.state === 'ENDED',
    ),
  );

  return [...actives, ...ended];
});

const categoriesWithCount = computed(() =>
  visibleCategories.value.filter((cat) => (cat.count ?? 0) > 0),
);

function postUrl(post: WpStylePost): string {
  return `/blog/${post.slug}`;
}

function categoryNameOfPost(post: WpStylePost): string {
  const firstId = post.categories[0];

  if (!firstId) return 'GERAL';

  return wpCategories.value.find((c) => c.id === firstId)?.name || 'GERAL';
}

async function getPosts() {
  isLoading.value = true;
  isLoadingCategories.value = true;

  try {
    const { categories } = await fetchBlogPostsHandler({});

    const promoId = getPromotionsCategoryId();
    const promoCat =
      categories.find((c) => c.id === promoId) ??
      categories.find((c) => isPromoCategoryName(c.name));

    promoCategoryId.value = promoCat?.id ?? null;
    promoSlugs.value = new Set((promoCat?.posts ?? []).map((p) => p.slug));

    const mapped = mapCategoriesToWp(categories);

    wpPosts.value = mapped.posts;
    wpCategories.value = mapped.categories;
  } catch (error) {
    console.error('Erro ao buscar posts:', error);
  } finally {
    isLoading.value = false;
    isLoadingCategories.value = false;
  }
}

async function loadDetail(slug: string) {
  if (!slug) {
    detailItem.value = null;
    isLoadingDetail.value = false;

    return;
  }

  isLoadingDetail.value = true;

  try {
    const { post } = await fetchBlogPostHandler(slug);

    if (!post) {
      detailItem.value = null;

      return;
    }

    detailItem.value = mapPostToWp(post, [], post.html_content || '');

    if (import.meta.client) blogPostClickEvent(slug);
  } catch (err) {
    console.error('[BlogCore] failed to load post detail', err);
    detailItem.value = null;
  } finally {
    isLoadingDetail.value = false;
  }
}

watch(
  postSlug,
  (slug) => {
    if (import.meta.client) loadDetail(slug);
  },
  { immediate: true },
);

async function refreshList() {
  clearBlogPostsCache();
  await getPosts();
}

function selectCategory(categoryId: string | null) {
  selectedCategory.value = categoryId;
}

const router = useRouter();

function isPostPath(path: unknown): boolean {
  if (typeof path !== 'string') return false;

  const clean = path.split('?')[0]?.split('#')[0] ?? '';

  return /^\/(blog|promotions)\/[^/]+\/?$/.test(clean);
}

function onBackClick() {
  if (postSlug.value) {
    navigateTo({ path: '/blog' });

    return;
  }

  const previousPath = (router.options.history.state?.back ?? '') as string;

  if (isPostPath(previousPath)) {
    navigateTo('/');

    return;
  }

  if (typeof window !== 'undefined' && window.history.length > 1) {
    router.back();

    return;
  }

  navigateTo('/');
}

const { isMobile } = useLayout();
const { openPreview } = useGameMobilePreview();

const contentRef = ref<HTMLElement | null>(null);
let cleanupWpScripts: (() => void) | null = null;
let cleanupGameLinks: (() => void) | null = null;

const RESERVED_GAMES_SEGMENTS = [
  'category',
  'providers',
  'live',
  'jogos-do-padrinho',
];

function extractGameSlug(href: string): string | null {
  let pathname = '';

  try {
    pathname = new URL(href, window.location.origin).pathname;
  } catch {
    pathname = (href.split('?')[0] ?? '').split('#')[0] ?? '';
  }

  const match = pathname.match(/^\/games\/([^/]+)\/([^/]+)\/?$/);

  if (!match) return null;

  const [, provider, game] = match;

  if (!provider || !game) return null;
  if (RESERVED_GAMES_SEGMENTS.includes(provider)) return null;

  return `${provider}/${game}`;
}

function interceptGameLinks(container: HTMLElement): () => void {
  async function handleClick(e: MouseEvent) {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }

    const anchor = (e.target as HTMLElement).closest('a');

    if (!anchor) return;
    if (!container.contains(anchor)) return;

    const href = anchor.getAttribute('href') ?? '';
    const slug = extractGameSlug(href);

    if (!slug) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (!isMobile.value) {
      const [provider, game] = slug.split('/');

      if (provider && game) {
        navigateTo({
          name: 'games-provider-game',
          params: { provider, game },
        });
      }

      return;
    }

    try {
      const { getApostatudoApiUrl } = await import(
        '~/utils/apostatudo/get-apostatudo-api-url'
      );

      const res = await fetch(`${getApostatudoApiUrl()}/games?slug=${slug}`, {
        headers: { Accept: 'application/json' },
      });

      const data = await res.json();

      openPreview({
        id: data.id,
        slug: data.slug ?? slug,
        name: data.name,
        image: data.image,
        provider: data.provider,
        provider_id: data.provider_id,
        reference: data.reference,
        casino_game_type_id: data.casino_game_type_id,
        demo: data.demo,
        rtp: data.rtp,
        tags: data.tags ?? [],
        ribbon: data.ribbon ?? null,
      } as any);
    } catch {}
  }

  document.addEventListener('click', handleClick, true);

  return () => document.removeEventListener('click', handleClick, true);
}

watch(
  () => singleItem.value?.content?.rendered,
  async (content) => {
    cleanupWpScripts?.();
    cleanupGameLinks?.();
    cleanupWpScripts = null;
    cleanupGameLinks = null;
    if (!content) return;
    await nextTick();
    if (contentRef.value) {
      cleanupWpScripts = executeWpScripts(contentRef.value);
      cleanupGameLinks = interceptGameLinks(contentRef.value);
    }
  },
  { flush: 'post', immediate: true },
);

onUnmounted(() => {
  cleanupWpScripts?.();
  cleanupGameLinks?.();
});

useSeoMeta({
  title: () => singleItem.value?.title?.rendered || undefined,
  description: () =>
    singleItem.value?.yoast_head_json?.og_description || undefined,
  ogTitle: () => singleItem.value?.title?.rendered || undefined,
  ogDescription: () =>
    singleItem.value?.yoast_head_json?.og_description || undefined,
  ogImage: () =>
    singleItem.value?.yoast_head_json?.og_image?.[0]?.url || undefined,
  twitterCard: 'summary_large_image',
});

onMounted(() => {
  getPosts();
});
</script>

<template>
  <div class="blog-wrapper">
    <section class="blog-container">
      <BaseScreenHeader
        heading-tag="h1"
        :is-refreshing="isLoading"
        :on-back="onBackClick"
        :on-refresh="!postSlug ? refreshList : undefined"
        :title="postSlug ? $t('casino.game.go_back') : $t('sidebar.blog')"
      />

      <div
        v-if="!postSlug && !isLoadingCategories"
        class="blog-tabs"
      >
        <button
          :class="['tab', { active: selectedCategory === null }]"
          @click="selectCategory(null)"
        >
          <span class="tab-label">Todas</span>
          <div class="tab-badge">
            <span class="tab-count">{{ visiblePosts.length }}</span>
          </div>
        </button>
        <button
          v-for="category in categoriesWithCount"
          :key="category.id"
          :class="['tab', { active: selectedCategory === category.id }]"
          @click="selectCategory(category.id)"
        >
          <span class="tab-label">{{ category.name }}</span>
          <div class="tab-badge">
            <span class="tab-count">{{ category.count }}</span>
          </div>
        </button>
      </div>

      <section
        v-if="postSlug"
        class="blog-view blog-content-sandbox"
      >
        <div
          v-if="isLoadingDetail"
          class="loading"
        >
          <div
            class="load skeleton-loading"
            style="height: 400px"
          ></div>
          <div
            v-for="(_n, index) in 30"
            :key="`skeleton-${index}`"
            class="load skeleton-loading"
            style="height: 20px; margin: 15px 0"
          ></div>
        </div>

        <div
          v-if="singleItem?.content?.rendered"
          ref="contentRef"
          class="cactus-blog content wp-content"
        >
          <div
            class="blog-content-sandbox"
            v-html="
              scopeWpHtml(singleItem.content.rendered, '.blog-content-sandbox')
            "
          ></div>
        </div>

        <div
          v-if="!singleItem && !isLoadingDetail"
          class="alert alert-warning"
        >
          <span>{{ $t('no_posts_found') }}</span>
        </div>
      </section>

      <section
        v-else-if="visiblePosts && visiblePosts.length"
        class="blog-section"
      >
        <nuxt-link
          v-if="!selectedCategory && featuredPost"
          :key="featuredPost.slug"
          class="featured-banner hidden md:flex"
          :draggable="false"
          :to="postUrl(featuredPost)"
        >
          <div class="featured-content">
            <div
              v-if="featuredPost.yoast_head_json.og_image.length"
              class="featured-image-container"
              style="outline: 1px solid #ff450c"
            >
              <div
                v-if="featuredPost.categories.length"
                class="featured-category-tag"
                style="background: #ff450c"
              >
                <div
                  class="category-label"
                  style="color: #ffffff"
                >
                  {{ categoryNameOfPost(featuredPost) }}
                </div>
              </div>
              <img
                :alt="featuredPost.title.rendered"
                class="featured-image"
                :draggable="false"
                :src="featuredPost.yoast_head_json.og_image[0]?.url"
              />
            </div>

            <div class="featured-info">
              <div class="featured-text">
                <div
                  v-if="featuredPost.title.rendered"
                  class="featured-title"
                >
                  <div>{{ featuredPost.title.rendered }}</div>
                </div>

                <div class="featured-excerpt">
                  {{ featuredPost.excerpt.rendered }}
                </div>
              </div>

              <div class="featured-footer">
                <div class="featured-date">
                  <NuxtIcon
                    class="featured-date-icon"
                    name="blog/calendar"
                  />
                  <div class="featured-date-text">
                    {{
                      featuredPost.date
                        ? new Date(featuredPost.date).toLocaleDateString(
                            'pt-BR',
                            {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric',
                            },
                          )
                        : ''
                    }}
                  </div>
                </div>
                <div class="featured-read-more">
                  <div class="featured-read-more-text">LER MAIS</div>
                  <NuxtIcon
                    class="featured-read-more-icon"
                    name="mission/button/participar"
                  />
                </div>
              </div>
            </div>
          </div>
        </nuxt-link>

        <nuxt-link
          v-for="(post, index) in regularPosts"
          :key="post.slug"
          :class="[
            'post-card',
            {
              'md:hidden': !selectedCategory && index === 0 && isBannerSpecial,
              'post-card-ended': post.state === 'ENDED',
            },
          ]"
          :draggable="false"
          :to="postUrl(post)"
        >
          <div class="post-content">
            <div
              class="post-image-wrapper"
              :style="{
                border: `1px solid ${
                  post.state === 'ENDED' ? '#6b7280' : '#ff450c'
                }`,
              }"
            >
              <div
                v-if="post.ribbon?.text"
                class="post-ribbon"
                :style="{
                  background: post.ribbon.bg_color || undefined,
                  color: post.ribbon.font_color || undefined,
                }"
              >
                {{ post.ribbon.text }}
              </div>

              <div
                v-if="post.yoast_head_json.og_image.length"
                class="post-image"
              >
                <div
                  v-if="!selectedCategory && post.categories.length"
                  class="post-category-tag"
                  style="background: #ff450c"
                >
                  <div
                    class="category-label"
                    style="color: #ffffff"
                  >
                    {{ categoryNameOfPost(post) }}
                  </div>
                </div>
                <img
                  :alt="post.title.rendered"
                  :draggable="false"
                  :src="post.yoast_head_json.og_image[0]?.url"
                />
              </div>
            </div>

            <div class="post-info">
              <div class="post-text">
                <div
                  v-if="post.title.rendered"
                  class="post-title"
                >
                  <div>{{ post.title.rendered }}</div>
                </div>

                <div class="post-excerpt">
                  {{ post.excerpt.rendered }}
                </div>
              </div>
            </div>
          </div>

          <div class="md:hidden post-footer-mobile">
            <div class="post-date">
              <NuxtIcon
                class="date-icon"
                name="blog/calendar"
              />
              <div class="date-text">
                {{
                  post.date
                    ? new Date(post.date).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })
                    : ''
                }}
              </div>
            </div>
            <div class="post-read-more">
              <div class="read-more-text">LER MAIS</div>
              <NuxtIcon
                class="read-more-icon"
                name="mission/button/participar"
              />
            </div>
          </div>

          <div class="hidden md:flex post-footer">
            <div class="post-date">
              <NuxtIcon
                class="date-icon"
                name="blog/calendar"
              />
              <div class="date-text">
                {{
                  post.date
                    ? new Date(post.date).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })
                    : ''
                }}
              </div>
            </div>
            <div class="post-read-more">
              <div class="read-more-text">LER MAIS</div>
              <NuxtIcon
                class="read-more-icon"
                name="mission/button/participar"
              />
            </div>
          </div>
        </nuxt-link>
      </section>
      <section
        v-else-if="isLoading"
        class="blog-section loading"
      >
        <div
          v-for="(_n, index) in 10"
          :key="`skeleton-${index}`"
          class="post skeleton-loading"
          style="height: 280px"
        ></div>
      </section>
      <section
        v-else
        class="blog-section"
      >
        <div class="alert alert-warning">
          <span>{{ $t('no_posts_found') }}</span>
        </div>
      </section>
    </section>
  </div>
</template>

<style scoped lang="scss">
.blog-view {
  @apply flex justify-between items-stretch flex-wrap gap-[15px] md:gap-[25px] text-left;
  user-select: none;
  -webkit-user-select: none;

  a,
  img {
    -webkit-user-drag: none;
  }
  .loading {
    @apply w-full;
    .load {
      @apply flex-1 min-w-[90%] max-w-[100%] rounded overflow-hidden;
    }
  }
  .thumb {
    @apply w-full overflow-hidden relative object-cover object-center bg-bg-secondary flex items-center justify-center rounded;
    img {
      @apply min-w-full min-h-full max-h-full w-auto h-auto object-cover object-center;
    }
  }
  .title {
    @apply text-primary text-2xl font-semibold -mt-2;
    font-size: 24px;
    line-height: 1.3;

    :deep(*) {
      font-size: 24px !important;
      line-height: 1.3 !important;
      margin: 0 !important;
    }
  }
  .content {
    @apply w-full overflow-hidden;
    img {
      @apply max-w-full;
    }
  }
}

.blog-section {
  @apply flex justify-start items-stretch flex-wrap gap-[15px] md:gap-[25px];
  user-select: none;
  -webkit-user-select: none;

  a,
  img {
    -webkit-user-drag: none;
  }

  .featured-banner {
    @apply w-full rounded-[10px] overflow-hidden cursor-pointer no-underline;
    background-color: rgb(3, 7, 18);
    outline: 1px solid rgb(38, 38, 38);
    outline-offset: -1px;
    transition: opacity 0.2s;
    flex-direction: column;

    &:hover {
      @apply opacity-90;
    }

    .featured-content {
      @apply p-2.5 md:p-5 flex flex-col gap-5;

      .featured-image-container {
        @apply w-full rounded-[20px] relative flex flex-col justify-start items-start;
        outline-offset: -1px;
        overflow: hidden;
        aspect-ratio: 1240 / 541;
        max-height: 500px;

        .featured-category-tag {
          @apply px-3.5 py-2 rounded-br-[10px] inline-flex justify-center items-center gap-[5px];
          @apply absolute;
          top: -1px;
          left: -1px;
          z-index: 20;
          border-top-left-radius: 20px;
          box-shadow: 0 0 0 1px #ff450c;

          .category-label {
            @apply text-white text-sm font-extrabold leading-[9px];
            font-family: 'Plus Jakarta Sans', sans-serif;
          }
        }

        .featured-image {
          @apply w-full h-full object-cover;
        }
      }

      .featured-info {
        @apply flex justify-between items-center gap-5;

        .featured-text {
          @apply flex-1 flex flex-col justify-center items-start gap-2.5;

          .featured-title {
            @apply w-full text-white font-bold;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 30px !important;
            line-height: 1.25 !important;

            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            padding-bottom: 2px;

            :deep(:where(*)) {
              font-size: inherit !important;
              line-height: inherit !important;
              font-weight: 700 !important;
              margin: 0 !important;
            }
          }

          .featured-excerpt {
            @apply w-full text-white/50 font-medium;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 14px;
            line-height: 1.4;

            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;

            :deep(p) {
              margin: 0 !important;
              display: inline;
            }
          }
        }

        .featured-footer {
          @apply pl-2.5 md:pl-5 pr-2.5 py-2.5 bg-[#1a1d29] rounded-[10px] flex justify-between items-center gap-4;
          flex-shrink: 0;
          min-width: fit-content;

          .featured-date {
            @apply px-[5px] flex justify-start items-center gap-2;

            .featured-date-icon {
              @apply text-white/50;
              font-size: 16px;
            }

            .featured-date-text {
              @apply text-white/50 text-xs font-bold leading-4;
              font-family: 'Plus Jakarta Sans', sans-serif;
              white-space: nowrap;
            }
          }

          .featured-read-more {
            @apply h-10 px-5 rounded-md flex justify-center items-center gap-1.5;
            background: radial-gradient(
              ellipse 138% 168.3% at 36.76% -16.67%,
              #ff704a 0%,
              #ff3400 100%
            );
            outline: 0.5px solid rgba(255, 195, 195, 0.3);
            outline-offset: -0.5px;

            .featured-read-more-text {
              @apply text-white text-sm font-extrabold leading-3;
              font-family: 'Plus Jakarta Sans', sans-serif;
            }

            .featured-read-more-icon {
              @apply text-white;
              font-size: 16px;
            }
          }
        }
      }
    }
  }

  .post-card {
    @apply p-2.5 md:p-5 rounded-[10px] backdrop-blur-[10px] overflow-hidden cursor-pointer no-underline;
    @apply w-full md:w-[calc(50%-12.5px)] xl:w-[calc(33.333%-16.667px)];
    background-color: rgb(3, 7, 18);
    outline: 1px solid rgb(38, 38, 38);
    outline-offset: -1px;
    transition: opacity 0.2s;
    display: flex;
    flex-direction: column;

    &:hover {
      @apply opacity-90;
    }

    &.post-card-ended {
      .post-image-wrapper img {
        filter: grayscale(1);
      }

      .post-text .post-title,
      .post-text .post-excerpt {
        @apply text-white/40;
      }
    }

    .post-content {
      @apply flex flex-col justify-start items-start gap-5;
      flex: 1;

      .post-image-wrapper {
        @apply w-full rounded-[10px] flex flex-col justify-between items-center;
        overflow: visible;
        position: relative;

        .post-ribbon {
          @apply px-3 py-1 text-[10px] font-extrabold uppercase rounded-md;
          font-family: 'Plus Jakarta Sans', sans-serif;
          letter-spacing: 0.5px;
          width: fit-content;
          position: absolute;
          top: 0;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 10;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
        }

        .post-image {
          @apply w-full relative overflow-hidden rounded-[10px] bg-black;
          aspect-ratio: 1240 / 541;

          .post-category-tag {
            @apply px-3.5 py-2 rounded-br-[10px] inline-flex justify-center items-center gap-[5px];
            @apply absolute;
            top: -1px;
            left: -1px;
            z-index: 20;
            border-top-left-radius: 10px;
            box-shadow: 0 0 0 1px #ff450c;

            .category-label {
              @apply text-white text-xs font-extrabold leading-[9px];
              font-family: 'Plus Jakarta Sans', sans-serif;
            }
          }

          img {
            @apply w-full h-full object-contain object-center rounded-[10px] block;
          }
        }
      }

      .post-info {
        @apply w-full flex flex-col justify-start items-start gap-0;

        .post-text {
          @apply w-full px-0 md:px-2.5 pb-0 flex flex-col justify-center items-center gap-2.5;

          .post-title {
            @apply w-full text-white font-bold;
            font-family: 'Plus Jakarta Sans', sans-serif;

            font-size: 24px !important;
            line-height: 1.2 !important;

            :deep(:where(*)) {
              font-size: 24px !important;
              line-height: 1.2 !important;
              font-weight: 700 !important;
              margin: 0 !important;
            }

            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .post-excerpt {
            @apply w-full text-white/50 font-medium;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 14px;
            line-height: 1.3;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }

    .post-footer-mobile {
      @apply w-full pl-2.5 pr-2.5 bg-white/5 rounded-[10px] flex justify-between items-center;
      padding-top: 6px;
      padding-bottom: 6px;
      margin-top: 12px;

      .post-date {
        @apply px-[5px] flex justify-start items-center gap-1.5;
        width: auto;
        flex: 1;

        .date-icon {
          @apply text-white/50;
          font-size: 14px;
        }

        .date-text {
          @apply text-white/50 font-bold;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 11px;
          line-height: 1.2;
        }
      }

      .post-read-more {
        @apply px-2.5 rounded-md flex justify-center items-center gap-1.5;
        height: 32px;
        width: auto;
        background: radial-gradient(
          ellipse 138% 168.3% at 36.76% -16.67%,
          #ff704a 0%,
          #ff3400 100%
        );
        outline: 0.5px solid rgba(255, 195, 195, 0.3);
        outline-offset: -0.5px;

        .read-more-text {
          @apply text-white font-extrabold;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 12px;
          line-height: 1.2;
        }

        .read-more-icon {
          @apply text-white;
          font-size: 14px;
        }
      }
    }

    .post-footer {
      @apply w-full pl-2.5 md:pl-5 pr-2.5 py-2.5 bg-white/5 rounded-[10px] flex justify-between items-center;
      margin-top: 12px;

      @media (min-width: 768px) {
        margin-top: 20px;
      }

      .post-date {
        @apply px-[5px] md:px-[5px] flex justify-start items-center gap-2;
        width: auto;
        flex: 1;
        margin-top: 0;
        padding: 5px;
        background: transparent;
        border-radius: 0;

        .date-icon {
          @apply text-white/50;
          font-size: 16px;
        }

        .date-text {
          @apply text-white/50 text-xs font-bold leading-4;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
      }

      .post-read-more {
        @apply h-10 px-2.5 md:px-5 rounded-md flex justify-center items-center gap-2;
        width: auto;
        margin-top: 0;
        background: radial-gradient(
          ellipse 138% 168.3% at 36.76% -16.67%,
          #ff704a 0%,
          #ff3400 100%
        );
        outline: 0.5px solid rgba(255, 195, 195, 0.3);
        outline-offset: -0.5px;

        .read-more-text {
          @apply text-white text-sm font-extrabold leading-3;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .read-more-icon {
          @apply text-white;
          font-size: 16px;
        }
      }
    }
  }
}

.blog-wrapper {
  @apply w-full -mt-4 lg:-mt-5;
}

.blog-container {
  @apply mt-0 md:mt-0 mb-6 md:mb-10;
}

.blog-tabs {
  @apply flex justify-center items-center mb-4 border-b border-[#424344];
  gap: 7.5px;
  overflow-x: auto;
  flex-wrap: nowrap;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (min-width: 1024px) {
    @apply justify-start;
  }

  @media (max-width: 639px) {
    @apply justify-start;
    -webkit-overflow-scrolling: touch;
  }

  .tab {
    @apply px-[15px] flex-shrink-0 rounded-[10px] font-semibold border transition-colors duration-150 cursor-pointer;
    @apply flex items-center gap-[5px];
    height: 31px;
    background: #09112c;
    color: #fff;
    outline: none;
    box-shadow: none;
    font-size: 12.507px;
    border: 0.5px solid #30364d;
    font-weight: 300;

    &:hover {
      @apply opacity-80;
    }

    .tab-label {
      @apply text-white;
      white-space: nowrap;
    }

    &.active {
      color: #00c9ff;
      border: 1px solid #00c9ff;
      background: linear-gradient(90deg, #00c9ff00 0%, #00c9ff33 100%);
      font-weight: 700;

      .tab-label {
        color: #00c9ff;
      }
    }

    .tab-badge {
      @apply w-4 h-4 rounded-full flex items-center justify-center;
      background-color: #ff450c;

      .tab-count {
        @apply text-white text-[9.62px] font-semibold leading-3;
        font-family: 'Plus Jakarta Sans', sans-serif;
      }
    }

    @media (max-width: 639px) {
      @apply px-[10px] py-[5px];
      min-width: 70px;
      white-space: nowrap;
      flex: 0 0 auto;
    }
  }
}
</style>

<!-- A folha global do conteúdo do post (`.cactus-blog.content` & cia.) saiu
     daqui pra assets/css/blog-content.scss, importada em main.scss: ela ganhou
     um segundo consumidor (components/loyalty-hub/PostSheet.vue, que abre a
     promoção dentro de um modal). Mesmas regras, mesmo alcance global. -->
